import { defineRule, configure } from 'vee-validate';
import * as AllRules from '@vee-validate/rules';
import { localize, setLocale } from '@vee-validate/i18n';
import { getCardMask, getCountryCodeByNumber, getCountryByCode } from '@zotlo-shared/components/input/utils';
import { useSiteDataOnClient } from '@/composables/useSiteDataOnClient';

function updateValidationMessages(payload: { locale: string, messages: Record<string, any> }) {
  function transpileMessageValue(_: string, value: any) {
    if (typeof value === 'object' && value !== null) {
      const keys = Object.keys(value).sort().join(',');
      if (/body|end|loc|start|type/.test(keys)) {
        return value?.loc?.source || value;
      }
    }
    return value;
  }

  const localized = Object.entries(payload.messages).reduce((acc: Record<string, any>, [locale, messages]) => {
    const message = messages?.generate?.form?.validation?.rule;
    if (!message) return acc;

    acc[locale] = {
      messages: JSON.parse(JSON.stringify(message), transpileMessageValue)
    };
    return acc;
  }, {});

  configure({
    generateMessage: localize(localized)
  });

  setLocale(payload.locale);
}

export default defineNuxtPlugin(async (app) => {
  const { siteData } = useSiteDataOnClient();
  const { lang = 'tr' } = siteData.value || {};
  const locales = (app?.$i18n as any)?.getLocaleMessage(lang);

  for (const rule of Object.keys(AllRules.all)) {
    defineRule(rule, (AllRules as any)[rule]);
  }

  defineRule('expirationDate', (value: string) => {
    const pattern = /^(0[1-9]|1[0-2])[0-9]{2}$/;
    return pattern.test(value);
  });

  defineRule('card', (value: string) => {
    const card = getCardMask(value);
    return value.length === card.length;
  });

  // Luhn (mod 10) checksum: doubling every second digit from the right, reducing
  // results over 9 by 9 and expecting the total to be a multiple of 10.
  defineRule('luhn', (value: string) => {
    const digits = `${value ?? ''}`.replace(/\D/g, '');
    if (!digits) return true;

    let sum = 0;
    let double = false;

    for (let i = digits.length - 1; i >= 0; i--) {
      let digit = Number(digits[i]);

      if (double) {
        digit *= 2;
        if (digit > 9) digit -= 9;
      }

      sum += digit;
      double = !double;
    }

    return sum % 10 === 0;
  });

  defineRule('phoneFormat', (value: string, countryCode:string[]) => {
    const code = countryCode?.[0] || getCountryCodeByNumber(value, false);
    const country = getCountryByCode(code);
    const maxLength = country?.maxMaskLength || 16;
    const minLength = country?.minMaskLength || 7;

    if (value.length > maxLength || value.length < minLength) {
      return 'Invalid phone number format';
    }

    return true;
  });

  defineRule('cpfCnpj', (value: string) => {
    const digits = (value || '').replace(/\D/g, '');
    // CPF has 11 digits, CNPJ has 14 digits.
    if (digits.length === 11 || digits.length === 14) return true;
    return 'Invalid CPF/CNPJ number'
  })

  updateValidationMessages({ locale: lang, messages: { [lang]: locales } })
});

